import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function AuthPage() {
  const { t } = useTranslation();
  const { login, register, loginWithGoogle, user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate(user.onboarding ? "/profil" : "/onboarding");
  }, [user, navigate]);

  if (user) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        navigate("/profil");
      } else {
        await register(form.name, form.email, form.password);
        navigate("/onboarding");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'authentification");
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full bg-input border border-primary/20 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-primary focus:outline-none";

  return (
    <main className="min-h-[80vh] flex items-center justify-center px-6 relative z-10" data-testid="auth-page">
      <div className="w-full max-w-md bg-card border border-primary/15 rounded-lg p-8 sm:p-10">
        <h1 className="font-serif-display text-3xl tracking-tight">{mode === "login" ? t("auth.login") : t("auth.register")}</h1>
        <div className="gold-line my-6" />

        <button onClick={loginWithGoogle}
          className="w-full flex items-center justify-center gap-3 py-3 rounded-full border border-primary/30 text-sm text-foreground hover:bg-primary/10 transition-colors duration-200"
          data-testid="google-login-btn">
          <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#EA4335" d="M12 5.04c1.7 0 3.23.59 4.43 1.74l3.29-3.29C17.72 1.64 15.06.5 12 .5 7.31.5 3.26 3.19 1.28 7.12l3.85 2.99C6.08 7.24 8.8 5.04 12 5.04z"/><path fill="#4285F4" d="M23.49 12.27c0-.85-.08-1.67-.22-2.46H12v4.65h6.45a5.52 5.52 0 0 1-2.39 3.62l3.73 2.9c2.18-2.02 3.7-5 3.7-8.71z"/><path fill="#FBBC05" d="M5.13 14.39a7.06 7.06 0 0 1 0-4.28L1.28 7.12a11.5 11.5 0 0 0 0 10.26l3.85-2.99z"/><path fill="#34A853" d="M12 23.5c3.06 0 5.63-1.01 7.51-2.75l-3.73-2.9c-1.03.7-2.36 1.11-3.78 1.11-3.2 0-5.92-2.2-6.87-5.07l-3.85 2.99C3.26 20.81 7.31 23.5 12 23.5z"/></svg>
          {t("auth.google")}
        </button>

        <div className="flex items-center gap-4 my-6">
          <div className="flex-1 h-px bg-primary/15" /><span className="text-xs text-muted-foreground">{t("auth.or")}</span><div className="flex-1 h-px bg-primary/15" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={t("auth.name")} className={input} data-testid="auth-name-input" />
          )}
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder={t("auth.email")} className={input} data-testid="auth-email-input" />
          <input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            placeholder={t("auth.password")} className={input} data-testid="auth-password-input" />
          <button type="submit" disabled={busy}
            className="w-full py-3 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-[#16528A] transition-colors duration-200 disabled:opacity-50"
            data-testid="auth-submit-btn">
            {mode === "login" ? t("auth.submitLogin") : t("auth.submitRegister")}
          </button>
        </form>

        <button onClick={() => setMode(mode === "login" ? "register" : "login")}
          className="w-full text-center text-sm text-primary mt-6 hover:underline underline-offset-4"
          data-testid="auth-switch-mode-btn">
          {mode === "login" ? t("auth.switchToRegister") : t("auth.switchToLogin")}
        </button>
      </div>
    </main>
  );
}
